import { useCallback, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { AlertTriangle } from 'lucide-react';

// In-app replacement for window.confirm(). Native dialogs are silently
// auto-dismissed inside embedded previews/webviews (confirm() returns false
// with no prompt shown), which made destructive actions look like dead
// buttons. This renders a real dialog and resolves a promise instead.
//
//   const [confirm, confirmDialog] = useConfirm();
//   if (!(await confirm('Archive this item?'))) return;
//   ... and render {confirmDialog} somewhere in the component.
export function useConfirm() {
  const [state, setState] = useState(null);

  const confirm = useCallback((opts) => new Promise((resolve) => {
    const base = typeof opts === 'string' ? { message: opts } : (opts || {});
    setState({ title: 'Are you sure?', confirmLabel: 'Confirm', destructive: true, ...base, resolve });
  }), []);

  const close = (value) => {
    setState((s) => { if (s) s.resolve(value); return null; });
  };

  const dialog = (
    <Dialog open={!!state} onOpenChange={(open) => { if (!open) close(false); }}>
      <DialogContent className="rounded-2xl max-w-sm" data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2 text-base">
            {state?.destructive && <AlertTriangle size={17} strokeWidth={1.7} className="text-[#C4727F]" />}
            {state?.title}
          </DialogTitle>
        </DialogHeader>
        {state?.message && <p className="text-sm text-[#6B7280] leading-relaxed">{state.message}</p>}
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={() => close(false)} data-testid="confirm-cancel"
            className="px-4 h-9 rounded-lg text-xs font-medium text-[#6B7280] hover:bg-[#E8E6E1] transition-colors">
            Cancel
          </button>
          <button onClick={() => close(true)} data-testid="confirm-accept" autoFocus
            className={`px-4 h-9 rounded-lg text-xs font-medium text-white transition-colors ${
              state?.destructive ? 'bg-[#C4727F] hover:bg-[#a3505e]' : 'bg-[#3A8C8C] hover:bg-[#2E7A7A]'}`}>
            {state?.confirmLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );

  return [confirm, dialog];
}

// Same idea for window.prompt(), which is suppressed identically.
// Resolves to the entered string, or null if cancelled.
//
//   const [prompt, promptDialog] = usePrompt();
//   const name = await prompt({ title: 'New template', label: 'Name' });
//   if (!name) return;
export function usePrompt() {
  const [state, setState] = useState(null);
  const [value, setValue] = useState('');

  const prompt = useCallback((opts) => new Promise((resolve) => {
    const base = typeof opts === 'string' ? { title: opts } : (opts || {});
    setValue(base.defaultValue || '');
    setState({ title: 'Enter a value', confirmLabel: 'OK', ...base, resolve });
  }), []);

  const close = (val) => {
    setState((s) => { if (s) s.resolve(val); return null; });
  };

  const submit = () => {
    const v = value.trim();
    if (!v) return;          // nothing to submit
    close(v);
  };

  const dialog = (
    <Dialog open={!!state} onOpenChange={(open) => { if (!open) close(null); }}>
      <DialogContent className="rounded-2xl max-w-sm" data-testid="prompt-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] text-base">{state?.title}</DialogTitle>
        </DialogHeader>
        {state?.message && <p className="text-sm text-[#6B7280] leading-relaxed">{state.message}</p>}
        <Input autoFocus value={value} placeholder={state?.placeholder || ''}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
          className="h-9 rounded-lg border-[#D4D0C8] text-sm" data-testid="prompt-input" />
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={() => close(null)} data-testid="prompt-cancel"
            className="px-4 h-9 rounded-lg text-xs font-medium text-[#6B7280] hover:bg-[#E8E6E1] transition-colors">
            Cancel
          </button>
          <button onClick={submit} disabled={!value.trim()} data-testid="prompt-accept"
            className="px-4 h-9 rounded-lg text-xs font-medium text-white bg-[#3A8C8C] hover:bg-[#2E7A7A] disabled:opacity-40 transition-colors">
            {state?.confirmLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );

  return [prompt, dialog];
}

export default useConfirm;
